/**
 * mfa.ts (AUTH-MFA-01, 2026-09-02) — two-factor sign-in on top of Supabase
 * Auth's TOTP factors (authenticator apps: Google Authenticator, Microsoft
 * Authenticator, 1Password…).
 *
 * Vocabulary Supabase uses: a session is "aal1" after a password alone and
 * "aal2" once a code from an enrolled authenticator has been verified. A user
 * with a verified factor whose session is still aal1 must answer a challenge
 * before the app opens (MfaGate). Enrollment and the challenge share the
 * same small API below so the Settings card and the sign-in gate stay in step.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface MfaStatus {
  /** A verified authenticator exists for this account. */
  enrolled: boolean;
  factorId: string | null;
  currentLevel: "aal1" | "aal2" | null;
  nextLevel: "aal1" | "aal2" | null;
  /** Enrolled, but this session has not answered a code yet. */
  needsChallenge: boolean;
}

export async function getMfaStatus(): Promise<MfaStatus> {
  const [{ data: factors }, { data: aal }] = await Promise.all([
    supabase.auth.mfa.listFactors(),
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
  ]);
  const verified = (factors?.totp ?? []).find((f) => f.status === "verified") ?? null;
  const currentLevel = (aal?.currentLevel ?? null) as MfaStatus["currentLevel"];
  const nextLevel = (aal?.nextLevel ?? null) as MfaStatus["nextLevel"];
  return {
    enrolled: !!verified,
    factorId: verified?.id ?? null,
    currentLevel,
    nextLevel,
    needsChallenge: !!verified && currentLevel === "aal1" && nextLevel === "aal2",
  };
}

export const MFA_STATUS_KEY = ["mfa_status"] as const;

export function useMfaStatus(enabled = true) {
  return useQuery({ queryKey: MFA_STATUS_KEY, queryFn: getMfaStatus, enabled, staleTime: 30 * 1000 });
}

export function useInvalidateMfa() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: MFA_STATUS_KEY });
}

export interface Enrollment {
  factorId: string;
  /** SVG data URL to render as <img>. */
  qrCode: string;
  /** Manual-entry secret for authenticator apps that cannot scan. */
  secret: string;
}

/** Start enrolling an authenticator. Abandoned (unverified) factors are removed first so retries never pile up. */
export async function startEnroll(): Promise<Enrollment> {
  const { data: factors } = await supabase.auth.mfa.listFactors();
  for (const f of factors?.totp ?? []) {
    if (f.status !== "verified") await supabase.auth.mfa.unenroll({ factorId: f.id });
  }
  // issuer = the name the authenticator app shows (without it Supabase uses the
  // Site URL host, which read "localhost" during the build).
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "Pegasus", issuer: "Pegasus" });
  if (error || !data) throw new Error(error?.message ?? "Could not start enrollment");
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/** Prove possession of the authenticator: one challenge + verify with the 6-digit code. */
export async function verifyCode(factorId: string, code: string): Promise<void> {
  const clean = code.replace(/\s+/g, "");
  if (!/^\d{6}$/.test(clean)) throw new Error("Enter the 6-digit code from your authenticator app.");
  const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
  if (chErr || !ch) throw new Error(chErr?.message ?? "Could not start the check");
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code: clean });
  if (error) throw new Error(/invalid/i.test(error.message) ? "That code did not match. Codes change every 30 seconds; try the current one." : error.message);
}

export async function unenroll(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}
